import React from 'react';
import { Button, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ContactAvatarWithNameEmail } from '../contact_avatar_with_name_email/ContactAvatarWithNameEmail';
import EntityAddRequestStatusDisplay from '../entity_add_request_status_display/EntityAddRequestStatusDisplay';
import { ENTITY_ADD_REQUEST_STATUSES } from '../../constants/EntityAddRequestStatuses';
import { EDITABLE_ENTITY_ADD_REQUEST_STATUSES } from '../../types/entity_add_requests';

const formatRequestedDate = (dateString) =>
	dateString ? moment(dateString).format('MMM D, h:mma') : '—';

const summarizeLocations = (payload) => {
	if (nullSafeGet('allLocations', payload)) {
		return 'All Locations';
	}
	const count = nullSafeGetOrElse('locationIds.length', payload, 0);
	return `${count} Location${count === 1 ? '' : 's'}`;
};

const summarizeProblemTypes = (payload) => {
	if (nullSafeGet('allProblemTypes', payload)) {
		return 'All Problem Types';
	}
	const count = nullSafeGetOrElse('problemTypeIds.length', payload, 0);
	return `${count} Problem Type${count === 1 ? '' : 's'}`;
};

interface ColumnHandlers {
	canApprove: boolean;
	currentUserEmail: string;
	onReview: (record: any) => void;
	onEdit: (record: any) => void;
	onDelete: (record: any) => void;
	onView: (record: any) => void;
	onShowAudits: (record: any) => void;
}

export const getEntityAddRequestColumns = ({
	canApprove,
	currentUserEmail,
	onReview,
	onEdit,
	onDelete,
	onView,
	onShowAudits,
}: ColumnHandlers) => {
	const handleReview = (record) => (event) => {
		event.stopPropagation();
		onReview(record);
	};
	const handleEdit = (record) => (event) => {
		event.stopPropagation();
		onEdit(record);
	};
	const handleDelete = (record) => (event) => {
		event.stopPropagation();
		onDelete(record);
	};
	const handleView = (record) => (event) => {
		event.stopPropagation();
		onView(record);
	};
	const handleShowAudits = (record) => (event) => {
		event.stopPropagation();
		onShowAudits(record);
	};

	const requesterOrApproverColumn = canApprove
		? {
				title: 'Requested by',
				dataIndex: 'requestedByEmail',
				// Show the hydrated contact's avatar + name + email; fall back to the raw email.
				render: (email, record) => (
					<ContactAvatarWithNameEmail
						contact={nullSafeGet('requestedByContact', record)}
						fallbackEmail={email}
					/>
				),
		  }
		: {
				title: 'Requested on',
				dataIndex: 'createdAt',
				render: (text) => formatRequestedDate(text),
		  };

	return [
		{
			title: 'Supplier Info',
			dataIndex: ['payload', 'companyName'],
			render: (text, record) => {
				const payload = nullSafeGetOrElse('payload', record, {});
				return (
					<div className="flex flex-col">
						<span className="bodymedium text-character-primary">
							{nullSafeGetOrElse(
								'companyName',
								payload,
								nullSafeGetOrElse('displayName', record, '—')
							)}
						</span>
						<span className="footnoteregular text-character-secondary">
							{summarizeLocations(payload)} &middot; {summarizeProblemTypes(payload)}
						</span>
					</div>
				);
			},
		},
		{
			title: 'Contact Info',
			dataIndex: ['payload', 'contactEmail'],
			render: (text, record) => {
				const payload = nullSafeGetOrElse('payload', record, {});
				const name = `${nullSafeGetOrElse('contactFirstName', payload, '')} ${nullSafeGetOrElse(
					'contactLastName',
					payload,
					''
				)}`.trim();
				return (
					<div className="flex flex-col">
						<span className="bodyregular text-character-primary">{name || '—'}</span>
						<span className="footnoteregular text-character-secondary">
							{nullSafeGetOrElse('contactEmail', payload, '')}
						</span>
					</div>
				);
			},
		},
		requesterOrApproverColumn,
		{
			title: 'Status',
			dataIndex: 'status',
			render: (status, record) => (
				<div className="flex flex-col" style={{ gap: '4px' }}>
					<div className="flex flex-row items-center" style={{ gap: '8px' }}>
						<EntityAddRequestStatusDisplay status={status} />
						<Tooltip title="View request history">
							<InfoCircleOutlined
								onClick={handleShowAudits(record)}
								className="cursor-pointer text-character-secondary"
							/>
						</Tooltip>
						{canApprove && status === ENTITY_ADD_REQUEST_STATUSES.pending_review ? (
							<span className="footnoteregular text-character-secondary">
								Requested: {formatRequestedDate(nullSafeGet('createdAt', record))}
							</span>
						) : null}
					</div>
					{status === ENTITY_ADD_REQUEST_STATUSES.rejected &&
					nullSafeGet('rejectionReason', record) ? (
						<span className="footnoteregular italic text-character-secondary">
							“{nullSafeGet('rejectionReason', record)}”
						</span>
					) : null}
				</div>
			),
		},
		{
			title: '',
			dataIndex: 'action',
			align: 'left' as const,
			render: (text, record) => {
				const status = nullSafeGet('status', record);
				if (canApprove && status === ENTITY_ADD_REQUEST_STATUSES.pending_review) {
					return <Button onClick={handleReview(record)}>Review</Button>;
				}
				if (status === ENTITY_ADD_REQUEST_STATUSES.approved) {
					return <Button onClick={handleView(record)}>VIEW</Button>;
				}
				// The list is company-wide, but a requester can only edit/delete their OWN
				// non-approved requests (draft / pending / rejected).
				const isOwnRequest = nullSafeGet('requestedByEmail', record) === currentUserEmail;
				if (!canApprove && isOwnRequest && status !== ENTITY_ADD_REQUEST_STATUSES.approved) {
					// A request under review can no longer be edited — only draft / rejected are editable.
					const isEditable = EDITABLE_ENTITY_ADD_REQUEST_STATUSES.includes(status);
					const editButton = (
						<Button icon={<EditOutlined />} disabled={!isEditable} onClick={handleEdit(record)} />
					);
					return (
						<div className="flex flex-row" style={{ gap: '8px' }}>
							{isEditable ? (
								editButton
							) : (
								// A disabled Button suppresses hover events, so wrap it in a Tooltip + span
								// (the span ensures the hover still fires) to surface why editing is blocked.
								<Tooltip title="Pending requests can't be edited while they're under review.">
									<span className="inline-flex">{editButton}</span>
								</Tooltip>
							)}
							<Button icon={<DeleteOutlined />} onClick={handleDelete(record)} />
						</div>
					);
				}
				return null;
			},
		},
	];
};
